import {
  AUCTION_REQUEST_DECLINED,
  AUCTION_REQUEST_DECLINED_REASON_NO_CAPACITY,
  AUCTION_REQUEST_DECLINED_REASON_NO_CAPACITY_FOR_PATIENT,
  AUCTION_REQUEST_DECLINED_REASON_NO_CAPACITY_FOR_PATIENT_H2H,
  AUCTION_REQUEST_DECLINED_REASON_OTHER,
  AUCTION_REQUEST_DECLINED_REASON_PAYER,
  AUCTION_REQUEST_DECLINED_REASON_UNATTRACTIVE_LOCATION,
  AUCTION_REQUEST_REJECTED,
  AUCTION_REQUEST_UNAVAILABLE,
  AUCTION_STATUS_ACCEPTED,
  AUCTION_STATUS_CANCELLED,
  AUCTION_STATUS_IN_PROGRESS,
  AUCTION_STATUS_MANUALLY_RUNNING,
  AUCTION_STATUS_NOT_STARTED,
  AUCTION_STATUS_SUCCESS,
  GENDER,
  INFECTION_AND_GERMS_REQUIRES_ISOLATION,
  NO_CAPACITY_FOR_CARELEVEL,
  NO_CAPACITY_FOR_GENDER,
  NO_CAPACITY_FOR_INFECTION,
  NO_CAPACITY_FOR_ROOM,
  NO_CAPACITY_FOR_SERVICE,
  NO_CAPACITY_FOR_SOLUTION,
  NO_CAPACITY_FOR_SPECIALIZATION,
  SEARCH_ACTION_DISPLAY_FILES_TAB,
  SEARCH_ACTION_RESTART_SEARCH,
  SEARCH_ACTION_START_AUTOMATIC_SEARCH,
  SEARCH_ACTION_START_MANUAL_SEARCH,
  SEARCH_TYPE_CARE,
  SEARCH_TYPE_HOME_CARE,
  SEARCH_TYPE_HOSPITAL,
  SEARCH_TYPE_MEDICAL_SUPPLIES,
  SEARCH_TYPE_PSYCHIATRIC_CARE,
  SEARCH_TYPE_REHABILITATION,
  TRACK_EVENTS,
} from "core/consts";
import { getName } from "core/model/accounts";
import {
  Address,
  Auction,
  AuctionRequest,
  AuctionResponse,
  Careprovider,
  Careseeker,
  Event,
  EventContext,
  GetOntologyType,
  NoCapacityReason,
  Patient,
  PatientWhitelistProps,
  PatientWhitelistScope,
  SearchAction,
  SearchActionType,
  SearchType,
  TrackEventFn,
} from "core/types";
import { format, fromUnixTime } from "date-fns";
import mergeWith from "lodash/mergeWith";
import { useTranslations } from "translations";
import Translations from "translations/types";
import { ONTOLOGIES } from "../utils/ontologies";
import { useGetTranslationForSearchType } from "../utils/ontologies/hooks";
import { getAuctionStopReasonTranslation } from "./stopReasons";

export const AUCTION_RUNNING_STATUSES = [
  AUCTION_STATUS_IN_PROGRESS,
  AUCTION_STATUS_MANUALLY_RUNNING,
  AUCTION_STATUS_ACCEPTED,
];

export const isAuctionRunning = (status: number | null | undefined) =>
  (status && AUCTION_RUNNING_STATUSES.includes(status)) || false;

export const useGetAuctionStopReason = (auction: Auction) => {
  const translations = useTranslations();
  const getTranslationForSearchType = useGetTranslationForSearchType();
  const searchType = auction?.search_type;
  const stopReason = auction?.cancel_auction?.reason;
  const notNeededTranslation = getTranslationForSearchType({
    receiverType: "receiverTypeSingular",
    searchType: searchType,
    translationKey: "patient.stopAuction.notNeeded",
  });
  const reason = getAuctionStopReasonTranslation({
    notNeededTranslation,
    stopReason,
    translations,
  });
  return reason;
};

export const getAuctionRequestStatus = (
  auctionRequest: AuctionRequest | null | undefined,
) => {
  if (auctionRequest?.status == null) return AUCTION_REQUEST_UNAVAILABLE;

  return auctionRequest.status;
};

export function getReasonTranslation({
  getOntology,
  reason,
  translations,
  value,
}: {
  getOntology: GetOntologyType;
  reason: number;
  translations: Translations;
  value: any;
}) {
  switch (reason) {
    case NO_CAPACITY_FOR_SPECIALIZATION:
      return getOntology({ key: value, type: "specializations" });
    case NO_CAPACITY_FOR_INFECTION:
      if (value === INFECTION_AND_GERMS_REQUIRES_ISOLATION) {
        return getOntology({
          key: value,
          type: ONTOLOGIES.INFECTION_AND_GERMS,
        });
      }
      return `${
        translations.auctionResponse.noCapacityReasons.treatmentOfPatientsWith
      } ${getOntology({ key: value, type: ONTOLOGIES.INFECTION_AND_GERMS })}`;
    case NO_CAPACITY_FOR_SERVICE:
      return getOntology({ key: value, type: ONTOLOGIES.SERVICE });
    case NO_CAPACITY_FOR_SOLUTION:
      return getOntology({ key: value, type: ONTOLOGIES.SOLUTION });
    case NO_CAPACITY_FOR_ROOM:
      return getOntology({ key: value, type: ONTOLOGIES.ROOM_TYPE });
    case NO_CAPACITY_FOR_GENDER:
      if (value == GENDER.FEMALE) {
        return translations.auctionResponse.noCapacityReasons.femaleRoom;
      }
      return translations.auctionResponse.noCapacityReasons.maleRoom;
    case NO_CAPACITY_FOR_CARELEVEL: {
      if (value == 0) {
        return translations.auctionResponse.noCapacityReasons
          .treatmentOfPatientsWithoutCarelevel;
      }
      return `${translations.auctionResponse.noCapacityReasons.treatmentOfPatientsWithCarelevel} ${value}`;
    }
    default:
      return "";
  }
}

function getReasonsText({
  getOntology,
  reasons,
  translations,
  withComma,
  withHyphen,
}: {
  getOntology: GetOntologyType;
  reasons: NoCapacityReason[] | undefined;
  translations: Translations;
  withComma?: boolean;
  withHyphen?: boolean;
}) {
  const reasonText: string[] = [];

  if (!reasons) return "";
  if (reasons)
    reasons.forEach(({ type, values }) =>
      values.forEach((value: any) => {
        reasonText.push(
          `\n${withHyphen ? "- " : ""}` +
            getReasonTranslation({
              getOntology,
              reason: type,
              translations,
              value,
            }),
        );
      }),
    );
  return reasonText.join(withComma ? ", " : " ");
}

function extractMinimalPatientInput(patient?: Patient):
  | {
      careseeker: {
        address: {
          country: Address["country"];
        };
        id: number;
        type: Careseeker["type"];
      };
      id: number;
    }
  | undefined {
  if (!patient?.careseeker) {
    return undefined;
  }

  const { address, type } = patient.careseeker;

  return {
    careseeker: {
      address: { country: address?.country },
      id: patient.careseeker.id,
      type,
    },
    id: patient.id,
  };
}

export function getAuctionForDynamo(auction: Auction | undefined) {
  if (!auction) return;

  return {
    assessment_variant: auction.assessment_variant,
    id: auction.id,
    patient: extractMinimalPatientInput(auction.patient),
    search_type: auction.search_type,
    solutions: auction.solutions,
    specializations: auction.specializations,
  };
}

export function declineDetailsReason({
  getOntology,
  isSearchMergeTable,
  locale,
  options,
  translations,
  withComma,
  withHyphen,
}: {
  getOntology: GetOntologyType;
  isSearchMergeTable?: boolean;
  locale: Locale;
  options: AuctionResponse | EventContext | null | undefined;
  searchType: SearchType | undefined;
  translations: Translations;
  withComma?: boolean;
  withHyphen?: boolean;
}): string | null {
  if (options?.declined_reason) {
    switch (options.declined_reason) {
      case AUCTION_REQUEST_DECLINED_REASON_NO_CAPACITY_FOR_PATIENT: {
        const reasons = getReasonsText({
          getOntology,
          reasons: options.no_capacity_reasons,
          translations,
          withComma,
          withHyphen,
        });

        if (reasons) {
          return `${translations.auctionRequest.noCapacityForPatientReason}:${reasons}`;
        }
        return translations.auctionRequest.noCapacityForPatientReason;
      }
      case AUCTION_REQUEST_DECLINED_REASON_NO_CAPACITY_FOR_PATIENT_H2H: {
        if (options.message) {
          return `${translations.auctionRequest.noCapacityForPatientReason} - ${options.message}`;
        }
        return translations.auctionRequest.noCapacityForPatientReason;
      }
      case AUCTION_REQUEST_DECLINED_REASON_NO_CAPACITY:
        if (options.capacity_date) {
          return translations.auctionResponse.declinedReasonNoCapacity({
            date: format(fromUnixTime(options.capacity_date), "P", { locale }),
          });
        }
        return translations.auctionResponse.declinedReasonNoCapacityNoDate;
      case AUCTION_REQUEST_DECLINED_REASON_UNATTRACTIVE_LOCATION:
        return translations.auctionRequest.geographicallyUnattractive;
      case AUCTION_REQUEST_DECLINED_REASON_PAYER: {
        const message = options.message ? `\n${options.message}` : "";
        return `${translations.auctionResponse.declinedReasonPayer}${message}`;
      }
      case AUCTION_REQUEST_DECLINED_REASON_OTHER:
        if (options.message)
          return isSearchMergeTable
            ? options.message
            : translations.auctionResponse.declinedReasonOther({
                reasonDescription: options.message,
              });

        return null;
      default:
        return null;
    }
  }

  return null;
}

export const isAuctionSuccess = (status: number | null | undefined) =>
  status === AUCTION_STATUS_SUCCESS;

export const isRequestAvailable = (
  request: AuctionRequest | null | undefined,
) => {
  const requestStatus = getAuctionRequestStatus(request);
  const auctionStatus = request?.auction?.status;
  const auctionCancelledAndWon =
    [AUCTION_STATUS_CANCELLED, AUCTION_REQUEST_DECLINED].includes(
      requestStatus || -1,
    ) &&
    [AUCTION_STATUS_SUCCESS, AUCTION_STATUS_CANCELLED].includes(
      auctionStatus || -1,
    );
  return (
    requestStatus !== AUCTION_REQUEST_REJECTED &&
    !auctionCancelledAndWon &&
    requestStatus !== AUCTION_REQUEST_UNAVAILABLE
  );
};

export function getAuctionStatusString(status: number | null | undefined) {
  switch (status) {
    case AUCTION_STATUS_NOT_STARTED:
      return "not started";
    case AUCTION_STATUS_IN_PROGRESS:
      return "in progress";
    case AUCTION_STATUS_SUCCESS:
      return "success";
    case AUCTION_STATUS_CANCELLED:
      return "cancelled";
    case AUCTION_STATUS_MANUALLY_RUNNING:
      return "manually running";
    default:
      return "unknown";
  }
}

export const auctionSuccessEvent = (
  event: Event,
  translations: Translations,
  getOntology: GetOntologyType,
  auctionSuccessUnknownTranslation: string,
) => {
  const providerName =
    event.careprovider?.name ?? event?.context?.careprovider_name;
  const accountName = getName(event.account, getOntology) ?? "";
  if (event.context?.manual) {
    return providerName
      ? translations.timeline.auctionSuccessProviderKnown({
          accountName,
          name: providerName,
        })
      : auctionSuccessUnknownTranslation;
  }

  return translations.timeline.auctionSuccess;
};

const isObject = (v: any) => typeof v === "object" && v !== null;

export function mergeAuction(
  oldAuction: Auction,
  newAuction: Auction,
): Auction {
  const recursive = (oldValue: any, newValue: any): any => {
    if (Array.isArray(newValue)) return newValue;
    return isObject(newValue)
      ? mergeWith(oldValue, newValue, recursive)
      : newValue;
  };

  return mergeWith(oldAuction, newAuction, recursive);
}

export function measureAssessmentCompletion({
  auction,
  sessionId,
  step,
  trackEvent,
}: {
  auction: Readonly<Auction>;
  sessionId: string | undefined;
  step: string;
  trackEvent: TrackEventFn;
}) {
  trackEvent({
    name: TRACK_EVENTS.ASSESSMENT_COMPLETION,
    search_type: auction.search_type,
    session_id_from_assessment: sessionId,
    solutions: auction.solutions
      ?.clone()
      ?.sort((a, b) => a - b)
      .join(","),
    specializations: auction.specializations?.concat().sort().join(","),
    step,
    variant: auction.assessment_variant,
  });
}

export function validateScope({
  careseeker: careseekerProp,
  formInputValue,
}: PatientWhitelistProps) {
  return function validateScopeWithProps(s: PatientWhitelistScope) {
    const careseeker = careseekerProp ?? formInputValue?.patient?.careseeker;
    if (!careseeker) {
      console.error("no careseeker providered for whitelist validation");
    }

    if (
      s.search_type != null &&
      formInputValue != null &&
      formInputValue?.search_type != s.search_type
    )
      return false;

    if (s.country != null && careseeker?.address?.country != s.country)
      return false;

    if (s.sender_type != null && careseeker?.type != s.sender_type)
      return false;

    if (
      formInputValue != null &&
      s.solutions != null &&
      !s.solutions.some(
        (solution) => formInputValue?.solutions?.includes(solution),
      )
    )
      return false;

    return true;
  };
}

export function matchesWhitelistScopes(scopes: PatientWhitelistScope[]) {
  return (props: PatientWhitelistProps) => scopes.some(validateScope(props));
}

export const showPatientDistance = (searchType: SearchType | undefined) =>
  searchType &&
  !(
    [SEARCH_TYPE_MEDICAL_SUPPLIES, SEARCH_TYPE_HOME_CARE] as SearchType[]
  ).includes(searchType);

export const findSearchAction = (
  auction: Auction,
  actionType: SearchActionType,
) =>
  auction.search_actions?.find(
    (searchAction) => searchAction.action_type === actionType,
  );

/**
 * True when the auction lists any action the search UI uses to start or restart a search
 * ({@link SEARCH_ACTION_START_MANUAL_SEARCH}, {@link SEARCH_ACTION_START_AUTOMATIC_SEARCH},
 * {@link SEARCH_ACTION_RESTART_SEARCH}). Matches {@link findSearchAction} as used by
 * `useStartManualSearch` / `useStartAutomaticSearch` / `useRestartSearch` and the search
 * merge actions bar — presence in `search_actions`, not `context.disabled`.
 */
export function auctionHasStartOrRestartSearchAction(
  auction: Auction | null | undefined,
): boolean {
  if (!auction?.search_actions?.length) {
    return false;
  }
  return (
    !!findSearchAction(auction, SEARCH_ACTION_RESTART_SEARCH) ||
    !!findSearchAction(auction, SEARCH_ACTION_START_MANUAL_SEARCH) ||
    !!findSearchAction(auction, SEARCH_ACTION_START_AUTOMATIC_SEARCH)
  );
}

export const getSearchActions = (
  auction: Auction | null,
): Record<string, never> | { [action in SearchActionType]: SearchAction } =>
  auction?.search_actions?.reduce(
    (acc, currentAction) => ({
      ...acc,
      [currentAction.action_type]: currentAction,
    }),
    {},
  ) || {};

export function isFilesActivatedAtAuctionLevel(auction: Auction) {
  const { [SEARCH_ACTION_DISPLAY_FILES_TAB]: action } =
    getSearchActions(auction);
  return !!(action && !action.context?.disabled);
}

export const getSearchRadius = ({
  searchType,
}: {
  searchType: SearchType | null | undefined;
}) => {
  switch (searchType) {
    case SEARCH_TYPE_HOSPITAL:
      return 600;
    case SEARCH_TYPE_REHABILITATION:
      return 600;
    case SEARCH_TYPE_CARE:
      return 200;
    default:
      return 200;
  }
};

export function getRadiusOptions({
  searchType,
}: {
  searchType: SearchType | null | undefined;
}) {
  return {
    max: getSearchRadius({ searchType }),
    min: 5,
    step: 5,
    unit: "km",
  };
}

export function isRehabSearch(
  searchType: SearchType | undefined,
): searchType is typeof SEARCH_TYPE_REHABILITATION {
  return searchType === SEARCH_TYPE_REHABILITATION;
}
export function isCareSearch(
  searchType: SearchType | undefined,
): searchType is typeof SEARCH_TYPE_CARE {
  return searchType === SEARCH_TYPE_CARE;
}

export function isHospitalSearch(
  searchType: SearchType | undefined,
): searchType is typeof SEARCH_TYPE_HOSPITAL {
  return searchType === SEARCH_TYPE_HOSPITAL;
}

export function isHomeCareSearch(
  searchType: SearchType | undefined,
): searchType is typeof SEARCH_TYPE_HOME_CARE {
  return searchType === SEARCH_TYPE_HOME_CARE;
}

export function isMedicalSuppliesSearch(
  searchType: SearchType | undefined,
): searchType is typeof SEARCH_TYPE_MEDICAL_SUPPLIES {
  return searchType === SEARCH_TYPE_MEDICAL_SUPPLIES;
}

export function isPsychCareSearch(
  searchType: SearchType | undefined,
): searchType is typeof SEARCH_TYPE_PSYCHIATRIC_CARE {
  return searchType === SEARCH_TYPE_PSYCHIATRIC_CARE;
}

export const isHomeCareMedicalSupplies = (auction: Auction): boolean =>
  (
    [SEARCH_TYPE_HOME_CARE, SEARCH_TYPE_MEDICAL_SUPPLIES] as SearchType[]
  ).includes(auction.search_type);

export function hasAnyFacility(
  careproviderFacilities: Careprovider["facilities"] | undefined,
  requiredFacilities: number[],
): boolean {
  if (!careproviderFacilities) return false;

  return requiredFacilities.some((facility) =>
    careproviderFacilities.includes(facility),
  );
}

export function shouldRedactHouseNumber({
  search_type,
  share_patient_data_automatically,
}: {
  search_type: SearchType | undefined;
  share_patient_data_automatically: boolean | undefined;
}): boolean {
  return (
    (isRehabSearch(search_type) || isHospitalSearch(search_type)) &&
    !!share_patient_data_automatically
  );
}
